import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  CircularProgress,
  Backdrop,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useAccountPositionStore } from "../Store/accountPositionStore";
import AccountSummary from "../Component/AccountPositions/AccountSummary";
import AccountList from "../Component/AccountPositions/AccountList";
import AccountPositionsTable from "../Component/AccountPositions/AccountPositionsTable";
import AccountDeletedModal from "../Component/AccountPositions/AccountDeletedModal";
import AddAccountModal from "../Component/AccountPositions/AddAccountModal";
import BondDetailsModal from "../Component/BondDetails/BondDetailsModal";
import CustomizeColumns from "../Component/CustomizeColumns";
import { useAuth } from "../Context/AuthProvider";
import { useAccountWebSocketUpdates } from "../Component/AccountPositions/useAccountWebSocketUpdates";
import useSnackbar from "../Utils/useSnackbar";
import AddIcon from "@mui/icons-material/Add";
import { clearConfigIfOutdated, updateColumnConfigIfNeeded } from "../Utils/columnVersionManager";
import { getPreference, setPreference } from "../Utils/userPreferenceManger";
import { fetchSecurityInfo } from "../http/api"; // Add this import
import { tokens, withAlpha } from "../Common/designTokens"; // added token import and withAlpha
import { usePageSubscription } from "../Utils/WebSocketProvider.jsx";

const AccountPositionExplorer = () => {
  const theme = useTheme();
  const { token, userId } = useAuth();
  const { showError, SnackbarComponent } = useSnackbar();
  usePageSubscription("accountPositions");
  const {
    fetchAccountData,
    fetchAccountPositions,
    accounts,
    selectedAccount,
    setSelectedAccount,
    positions,
    pagination,
    isLoading,
    error,
    notification,
    clearNotification,
    setAddAccountModalOpen,
    isAddAccountModalOpen,
    accountsPagination, // Add this
    getAccountForEdit,
    isEditAccountModalOpen,          // NEW
    editingAccount,                  // NEW
  } = useAccountPositionStore();

  // Setup WebSocket updates for accounts
  useAccountWebSocketUpdates();

  // Add local loading state for account switching
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);

  // Add pagination state
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Track last shown error to prevent infinite notifications
  const lastShownError = useRef(null);

  // State for account deleted modal
  const [accountDeletedModalOpen, setAccountDeletedModalOpen] = useState(false);
  const [deletedAccountInfo, setDeletedAccountInfo] = useState(null);

  // State for bond details modal (like Inventory)
  const [bondDetailsOpen, setBondDetailsOpen] = useState(false);
  const [selectedBondDetails, setSelectedBondDetails] = useState(null);
  const [bondDetailsLoading, setBondDetailsLoading] = useState(false); // Loader state

  // State for column customization
  const [ccOpen, setCcOpen] = useState(false);
  const customizeButtonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditPositionsLoading, setIsEditPositionsLoading] = useState(false); // NEW
  const [positionsEditOpen, setPositionsEditOpen] = useState(false);          // NEW
  const [positionsEditAccount, setPositionsEditAccount] = useState(null);     // NEW
  const [lockHeaderFields, setLockHeaderFields] = useState(false);            // NEW
  const modalTitle = positionsEditOpen
    ? "Add/Edit Account Positions"
    : isEditAccountModalOpen
    ? "Edit Account"
    : "Create Demo Account";                                                  // NEW

  // Monitor store errors and show snackbar - but only once per unique error
  useEffect(() => {
    if (error && error !== lastShownError.current) {
      showError(error);
      lastShownError.current = error;
    }

    // Reset the last shown error when error is cleared
    if (!error) {
      lastShownError.current = null;
    }
  }, [error, showError]);

  // Format monetary values with $ sign and commas
  const formatMonetary = (value) => {
    if (value === undefined || value === null) return "---";
    return `$${Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Handler for CUSIP click (use API data, not rowData)
  const handleCusipClick = async (index, rowData) => {
    setBondDetailsLoading(true);
    setBondDetailsOpen(true);
    try {
      const details = await fetchSecurityInfo(token, rowData.cusip || rowData.security);
      setSelectedBondDetails(details);
    } catch (e) {
      setSelectedBondDetails({ error: true, message: e.message || "Failed to fetch bond details." });
    } finally {
      setBondDetailsLoading(false);
    }
  };

  // Define all available columns for account positions
  const allColumns = [
    // { key: "action_account_pos", name: "Actions" }, // NEW fixed first column
    { key: "cusip", name: "SecurityId" },
    { key: "securityDesc", name: "Security Description" }, // changed from description
    {
      key: "quantity",
      name: "Qty",
      numeric: true,
    },
    {
      key: "price",
      name: "Price",
      numeric: true,
    },
    {
      key: "securityMarketValue",
      name: "Market Value",
      numeric: true,
    },
    {
      key: "securityWeightPercentage",
      name: "Weight %",
      numeric: true,
      render: (value) =>
        value !== null && value !== undefined
          ? `${Number(value).toFixed(2)}%`
          : "---",
    },
    {
      key: "bondType",
      name: "Bond Type",
      render: (value) => value || "---",
    },
    {
      key: "maturityDate", // changed from maturity
      name: "Maturity Date",
    },
    {
      key: "moodyRating",
      name: "Moody's Rating",
      render: (value) => value || "---",
    },
    {
      key: "spRating", // changed from ratingSnp
      name: "S&P Rating",
      render: (value) => value || "---",
    },
    {
      key: "couponPer", // changed from coupon
      name: "Coupon",
      render: (value) => value ?? "---",
    },
    {
      key: "yield",
      name: "YTM",
      numeric: true,
    },
    {
      key: "yieldToWorst",
      name: "YTW",
      numeric: true,
    },
    {
      key: "accruedInterest",
      name: "Accrued Interest",
      numeric: true,
    },
    {
      key: "dv01", 
      name: "DV01",
      numeric: true,
    },
    {
      key: "duration",
      name: "$ Duration",
      numeric: true,
    },
    {
      key: "modifiedConvexity",
      name: "Convexity",
      numeric: true,
    },
    {
      key: "isCallable", // changed from callable
      name: "Callable",
    },
    {
      key: "callStartDate",
      name: "Call Date",
    },
    {
      key: "nextCallDate",
      name: "Next Call Date",
    },
    {
      key: "callEndDate",
      name: "Call End Date",
    },
  ];

  // Selected columns for the positions table - now using state
  const ensureActionColumnFirst = (cols) => {
    const actionCol = allColumns.find((c) => c.key === "action_account_pos");
    const filtered = (cols || []).filter((c) => c.key !== "action_account_pos");
    return actionCol ? [actionCol, ...filtered] : cols || [];
  };

  const [selectedColumns, setSelectedColumns] = useState(() => {
    // Check and update column configuration if needed
    clearConfigIfOutdated("account-positions", allColumns);
    const saved = getPreference("selectedColumns_account-positions");
    return ensureActionColumnFirst(saved || allColumns);
  });

  // Ensure default width for the actions column is set at the account positions level
  useEffect(() => {
    const widthKey = "columnWidths_account-positions";
    const existing = getPreference(widthKey) || {};
    const desiredWidth = 100;
    if (existing.action_account_pos !== desiredWidth) {
      setPreference(widthKey, { ...existing, action_account_pos: desiredWidth });
    }
  }, []);

  // Sort configuration
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Handler for customize columns button
  const handleCustomizeClick = (anchorEl) => {
    setAnchorEl(anchorEl);
    setCcOpen(true);
  };

  useEffect(() => {
    // Use the token from auth context to fetch account data with infinite loading
    if (token) {
      fetchAccountData(true, userId); // Reset and fetch first page for current user
    }
  }, [token, userId, fetchAccountData]);

  // Single fetch path for positions: account switch, pagination and sorting all
  // flow through here so the request always carries the sort the header shows.
  useEffect(() => {
    if (selectedAccount && token) {
      setIsLoadingPositions(true);
      const args = [selectedAccount, token, page, itemsPerPage];
      if (sortConfig.key && sortConfig.direction) {
        args.push(sortConfig.key, sortConfig.direction);
      }
      fetchAccountPositions(...args).finally(() => setIsLoadingPositions(false));
    }
  }, [
    selectedAccount,
    token,
    page,
    itemsPerPage,
    sortConfig.key,
    sortConfig.direction,
    fetchAccountPositions,
  ]);

  const handleSortChange = (key, direction) => {
    setSortConfig({ key, direction });
    setPage(0); // effect above refetches with the new sort
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setPage(0);
  };

  // Keep this method for normal account selection via clicking the account itself
  const handleAccountSelect = (accountId) => {
    setSelectedAccount(accountId);
    setPage(0);
  };

  // Filter positions for the selected account (in a real implementation)
  // Here we're just showing all positions as mock data
  const filteredPositions = positions;

  // Add effect to handle account deletion modal
  useEffect(() => {
    const handleShowAccountDeletedModal = (event) => {
      const { deletedAccount, isCurrentlyViewed } = event.detail;

      // Double-check: don't show modal if current user deleted the account
      if (deletedAccount?.deletedBy && deletedAccount.deletedBy == userId) {
        console.log(
          "Safety check: Current user deleted the account, not showing modal"
        );
        return;
      }

      setDeletedAccountInfo({ deletedAccount, isCurrentlyViewed });
      setAccountDeletedModalOpen(true);
    };

    window.addEventListener(
      "showAccountDeletedModal",
      handleShowAccountDeletedModal
    );

    return () => {
      window.removeEventListener(
        "showAccountDeletedModal",
        handleShowAccountDeletedModal
      );
    };
  }, [userId]); // Add userId to dependencies

  // Handle account deleted modal close
  const handleAccountDeletedModalClose = () => {
    setAccountDeletedModalOpen(false);

    // Validate deletedAccountInfo
    if (!deletedAccountInfo || !deletedAccountInfo.deletedAccount) {
      console.error(
        "[handleAccountDeletedModalClose] Invalid deletedAccountInfo:",
        deletedAccountInfo
      );
      setDeletedAccountInfo(null);
      return;
    }

    const { deletedAccount, isCurrentlyViewed } = deletedAccountInfo;

    // Validate deletedAccount has accountId
    if (!deletedAccount.accountId) {
      console.error(
        "[handleAccountDeletedModalClose] Invalid deletedAccount, missing accountId:",
        deletedAccount
      );
      setDeletedAccountInfo(null);
      return;
    }

    // Log the current state for debugging
    console.log(
      "[handleAccountDeletedModalClose] Handling modal close for deleted account:",
      deletedAccount.accountId
    );
    console.log(
      "[handleAccountDeletedModalClose] isCurrentlyViewed:",
      isCurrentlyViewed
    );
    console.log("[handleAccountDeletedModalClose] Current accounts:", accounts);

    // Since useAccountWebSocketUpdates already handles account removal and switching,
    // we only need to clean up the modal state unless additional logic is required
    if (isCurrentlyViewed) {
      // The WebSocket hook has already:
      // 1. Removed the account from the accounts array
      // 2. Switched to the next available account or cleared selection
      // 3. Fetched positions for the new account if applicable
      console.log(
        "[handleAccountDeletedModalClose] Deleted account was currently viewed, WebSocket hook handled state updates"
      );
    } else {
      console.log(
        "[handleAccountDeletedModalClose] Deleted account was not currently viewed, WebSocket hook handled state updates"
      );
    }

    // Clear deletedAccountInfo
    setDeletedAccountInfo(null);
  };
  const handleAddAccount = () => {
    console.log("clicked");
    setAddAccountModalOpen(true);
  };

  const resetPositionsEditState = () => { // NEW
    setPositionsEditOpen(false);
    setPositionsEditAccount(null);
    setLockHeaderFields(false);
  };

  const handleEditPositions = async () => {
    if (!selectedAccount || isEditPositionsLoading) return;
    setIsEditPositionsLoading(true);
    try {
      let accountForEdit = getAccountForEdit(selectedAccount);
      if (!accountForEdit) {
        await fetchAccountPositions(selectedAccount, token, page, itemsPerPage);
        accountForEdit = getAccountForEdit(selectedAccount);
      }
      if (accountForEdit) {
        setPositionsEditAccount(accountForEdit);
        setLockHeaderFields(true);
        setPositionsEditOpen(true);
      } else {
        showError("Unable to load account for editing.");
      }
    } catch (e) {
      showError(e.message || "Failed to open editor.");
    } finally {
      setIsEditPositionsLoading(false);
    }
  };

  return (
    <>
      {/* Keep the original snackbar for error handling */}
      <SnackbarComponent />

      {/* Add a new snackbar for notifications from the store */}
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={clearNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {notification && (
          <Alert
            onClose={clearNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        )}
      </Snackbar>

      {/* Update the loading condition to check both isLoading and whether we have any accounts */}
      {
      // isLoading && !accounts.length ? (
      //   <Box
      //     sx={{
      //       height: "calc(100vh - 80px)",
      //       display: "flex",
      //       justifyContent: "center",
      //       alignItems: "center",
      //     }}
      //   >
      //     <CircularProgress />
      //   </Box>
      // ) :
      // ( accounts.length === 0 && !accountsPagination.isLoadingMore && !isLoading )? (
      //   <Box
      //     sx={{
      //       height: "calc(100vh - 80px)",
      //       display: "flex",
      //       justifyContent: "center",
      //       alignItems: "center",
      //     }}
      //   >
      //     <Box
      //       sx={{
      //         width: 340,
      //         height: 242,
      //         borderRadius: "2px",
      //         border: "1px solid rgba(255, 255, 255, 0.1)",
      //         padding: "50px",
      //         display: "flex",
      //         flexDirection: "column",
      //         alignItems: "center",
      //         gap: "10px",
      //         backgroundColor: "rgba(33, 33, 33, 1)",
      //       }}
      //     >
      //       <Typography
      //         variant="h6"
      //         sx={{ color: "white", fontWeight: "bold", fontSize: "20px" }}
      //       >
      //         No Accounts Added
      //       </Typography>
      //       <Typography
      //         variant="body2"
      //         sx={{
      //           color: "rgba(255, 255, 255, 0.5)",
      //           fontSize: "16px",
      //           textAlign: "center",
      //         }}
      //       >
      //         Get started by adding your first demo account
      //       </Typography>
      //       <Button
      //         startIcon={<AddIcon />}
      //         variant="contained"
      //         color="primary"
      //         onClick={handleAddAccount}
      //         sx={{
      //           mt: 2,
      //           height: 40,
      //           borderRadius: "4px",
      //           padding: "12px 24px",
      //           "& .MuiButton-startIcon": {
      //             marginRight: "10px",
      //           },
      //         }}
      //       >
      //         Add Demo Account
      //       </Button>
      //     </Box>
      //   </Box>
      // ) : 
      (
        <Container
          maxWidth={false}
          sx={{
            py: 2,
            height: "calc(100vh - 80px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Full-screen loading overlay for account switching */}
          <Backdrop
            sx={{
              color: tokens.colors.white,
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: withAlpha('#000000', 0.7),
              flexDirection: "column",
              gap: 2,
            }}
            open={isLoadingPositions}
          >
            <CircularProgress sx={{ color: tokens.colors.white }} size={60} />
            <Typography variant="h6" sx={{ color: tokens.colors.white }}>
              Loading account positions...
            </Typography>
          </Backdrop>

          {/* Main content area with left and right sections */}
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              overflow: "hidden",
              gap: "10px",
            }}
          >
            {/* Left Section - Account List (Full Height) */}
            <Box
              sx={{
                width: "275px",
                flexShrink: 0,
                backgroundColor: tokens.colors.defaultGridRowColor, // panel bg follows design tokens
                border: `1px solid ${tokens.colors.bodyBorderColor50}`, // light border token
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {/* Pass both initial loading and filter loading states */}
              <AccountList
                accounts={accounts}
                selectedAccount={selectedAccount}
                onSelectAccount={handleAccountSelect}
                loading={isLoading}
              />
            </Box>

            {/* Right Section - Split into Top (Summary) and Bottom (Table) */}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                gap: "10px",
                height: "100%",
              }}
            >
              {/* Top Section - Account Summary */}
              <Box
                sx={{
                  flexShrink: 0,
                  backgroundColor: tokens.colors.defaultGridRowColor,
                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  px: 2,
                  py: 1,
                }}
              >
                <AccountSummary
                  accounts={accounts}
                  selectedAccount={selectedAccount}
                />
              </Box>

              {/* Bottom Section - Positions Table */}
              <Box
                sx={{
                  flexGrow: 1,
                  backgroundColor: tokens.colors.defaultGridRowColor,
                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <AccountPositionsTable
                  data={positions}
                  selectedColumns={selectedColumns}
                  allColumns={allColumns}
                  onCustomizeColumns={handleCustomizeClick}
                  loading={false} // Don't show loading in table during account switch
                  sortConfig={sortConfig}
                  onSort={handleSortChange}
                  page={page}
                  itemsPerPage={itemsPerPage}
                  totalElements={pagination.totalElements}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onCusipClick={handleCusipClick} // Pass handler for CUSIP click
                  onEditPositions={handleEditPositions}                     // NEW
                  disableEditPositions={
                    !selectedAccount || isEditPositionsLoading || isLoadingPositions
                  }                                                          // NEW
                />
              </Box>
            </Box>
          </Box>
        </Container>
      )}

      {/* Account Deleted Modal */}
      <AccountDeletedModal
        open={accountDeletedModalOpen}
        onClose={handleAccountDeletedModalClose}
        deletedAccount={deletedAccountInfo?.deletedAccount}
        isCurrentlyViewed={deletedAccountInfo?.isCurrentlyViewed}
      />

      {/* Add this modal at the root level so it's always available */}
      <AddAccountModal
        open={isAddAccountModalOpen || positionsEditOpen || isEditAccountModalOpen}
        editMode={positionsEditOpen || isEditAccountModalOpen}
        editAccount={positionsEditOpen ? positionsEditAccount : editingAccount}
        lockHeaderFields={positionsEditOpen}
        lockAccountFieldOnly={isEditAccountModalOpen && !positionsEditOpen}
        hidePositions={isEditAccountModalOpen && !positionsEditOpen}
        modalTitle={modalTitle}                                               // NEW
        onClose={resetPositionsEditState}
      />

      {/* Bond Details Modal for CUSIP click */}
      <BondDetailsModal
        open={bondDetailsOpen}
        onClose={() => setBondDetailsOpen(false)}
        bondDetails={selectedBondDetails}
        loading={bondDetailsLoading}
      />

      {/* Add CustomizeColumns component */}
      <CustomizeColumns
        open={ccOpen}
        onClose={() => setCcOpen(false)}
        allColumns={allColumns}
        selectedColumns={selectedColumns}
        setSelectedColumnsInventory={(cols) => setSelectedColumns(ensureActionColumnFirst(cols))} // NEW enforce first column
        anchorEl={anchorEl}
        sectionKey="account-positions"
        height={320} // Custom height for account positions table
      />
    </>
  );
};


export default AccountPositionExplorer;
